import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api';

const GalleryPage = () => {
  const [groupedPhotos, setGroupedPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchGalleryPhotos();
  }, []);

  // Handle event click - open modal instead of navigating
  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleImageClick = (photo, photoIndex) => {
    setSelectedImage(photo);
    setCurrentImageIndex(photoIndex);
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    if (selectedEvent && selectedEvent.photos) {
      const nextIndex = (currentImageIndex + 1) % selectedEvent.photos.length;
      setCurrentImageIndex(nextIndex);
      setSelectedImage(selectedEvent.photos[nextIndex]);
    }
  };

  const handlePrevImage = () => {
    if (selectedEvent && selectedEvent.photos) {
      const prevIndex = currentImageIndex === 0 ? selectedEvent.photos.length - 1 : currentImageIndex - 1;
      setCurrentImageIndex(prevIndex);
      setSelectedImage(selectedEvent.photos[prevIndex]);
    }
  };



  const fetchGalleryPhotos = async () => {
    try {
      setLoading(true);
      // Use grouped endpoint to get organized data
      const response = await axios.get('/api/gallery/grouped');
      // console.log('Gallery response:', response.data);
      
      // Process grouped events from API
      const photosByEvent = response.data.map(event => {
        // Get the first photo to determine event date
        const firstPhoto = event.photos[0];
        const eventDate = firstPhoto?.eventDate || firstPhoto?.createdAt;
        
        return {
          eventName: event.eventName,
          eventData: {
            _id: event.eventName,
            eventName: event.eventName,
            startDate: eventDate, // Use eventDate from first photo
            endDate: eventDate
          },
          photos: event.photos
        };
      });
      
      // Sort by event date
      const grouped = photosByEvent.sort((a, b) => {
        const dateA = a.eventData.startDate ? new Date(a.eventData.startDate) : new Date(0);
        const dateB = b.eventData.startDate ? new Date(b.eventData.startDate) : new Date(0);
        return dateB - dateA;
      });
      
      setGroupedPhotos(grouped);
    } catch (error) {
      // // console.log('Error fetching gallery photos:', error);
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Our <span className="text-orange-600">Gallery</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Capturing moments, creating memories
          </p>
        </div>

        {/* Gallery Content */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-600 text-lg">Loading gallery...</p>
          </div>
        ) : groupedPhotos.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-gray-400 mb-4">
              <svg className="w-24 h-24 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-2xl font-semibold text-gray-700 mb-2">No Photos Available</h3>
            <p className="text-gray-500">Gallery photos will appear here once uploaded.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groupedPhotos.map((group, groupIndex) => {
              // Get the first photo as representative image
              const representativePhoto = group.photos[0];
              
              return (
                <div
                  key={groupIndex}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105"
                  onClick={() => handleEventClick(group)}
                >
                  {/* Event Photo - Clean without any overlays */}
                  <div className="relative">
                    <img
                      src={representativePhoto.image}
                      alt={group.eventName}
                      className="w-full h-48 object-cover"
                      onError={e => { 
                        // Replace with alternative image instead of placeholder text
                        e.target.src = '/images/heroSection_img.jpg';
                      }}
                    />
                  </div>
                  
                  {/* Event Details Below Photo */}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-1">
                      {group.eventName}
                    </h3>
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                      <span>{group.photos.length} photo{group.photos.length !== 1 ? 's' : ''}</span>
                      <span>{new Date(group.eventData.startDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}</span>
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2">
                      Click to view all photos from this event
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Event Photos Modal */}
      {selectedEvent && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedEvent(null)}
        >
          <div className="relative max-w-6xl w-full max-h-[90vh] overflow-hidden bg-white rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{selectedEvent.eventName}</h3>
                </div>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Photos Grid */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {selectedEvent.photos.map((photo, photoIndex) => (
                  <div
                    key={photo.id || photoIndex}
                    className="relative group cursor-pointer transform transition-all duration-300 hover:scale-105"
                    onClick={() => handleImageClick(photo, photoIndex)}
                  >
                    <div className="relative overflow-hidden rounded-xl shadow-lg bg-white">
                      <img
                        src={photo.image}
                        alt={photo.title || selectedEvent.eventName}
                        loading="lazy"
                        className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={e => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Event Date: {new Date(selectedEvent.eventData.startDate).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
                <span className="text-sm text-gray-600">
                  {selectedEvent.photos.length} Photo{selectedEvent.photos.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-95 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
          <div className="relative max-w-7xl w-full max-h-[90vh] flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={handleCloseImage}
              className="absolute top-4 right-4 text-white hover:text-gray-300 p-2 rounded-lg hover:bg-white/20 transition-colors z-10"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Previous Button */}
            {selectedEvent && selectedEvent.photos && selectedEvent.photos.length > 1 && (
              <button
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 p-3 rounded-lg hover:bg-white/20 transition-colors z-10"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Next Button */}
            {selectedEvent && selectedEvent.photos && selectedEvent.photos.length > 1 && (
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 p-3 rounded-lg hover:bg-white/20 transition-colors z-10"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Image */}
            <img
              src={selectedImage.image}
              alt={selectedImage.title || selectedEvent?.eventName || 'Gallery Image'}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onError={e => {
                e.target.style.display = 'none';
              }}
            />

            {/* Image Counter */}
            {selectedEvent && selectedEvent.photos && selectedEvent.photos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-lg text-sm">
                {currentImageIndex + 1} / {selectedEvent.photos.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage; 